import '@learnforge/demo-kit/styles.css';
import './styles.css';

import * as THREE from 'three';
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { CSS2DRenderer, CSS2DObject } from 'three/addons/renderers/CSS2DRenderer.js';

import {
  resolveInitialTheme,
  applyTheme,
  onThemeMessage,
  mountLangPicker,
  mountTopbar,
  mountHelp,
  mountMobileDrawer,
  mountRotateNudge,
  mountThemeToggle,
  makeCollapsible,
  InfoBadge,
  createSliderRow,
  createStyleRow,
  createToggleRow,
  getShotId,
  markShotMode,
  readRequestedExperiment,
  writeExperimentUrl,
  notifyHostExperiment,
  onExperimentRequest,
  type SliderRowHandle,
  type TopbarHandle,
} from '@learnforge/demo-kit';

import { i18n } from './i18n';
import { FIGURES, type CutPlane, type FigureData, type SpecialLine, type Vertex } from './figures';

// ---------- Types ----------

type FigKey = keyof typeof FIGURES;

// ---------- Theme ----------

function readThemeBgHex(): number {
  const v = getComputedStyle(document.documentElement).getPropertyValue('--bg').trim();
  if (v.startsWith('#') && (v.length === 7 || v.length === 4)) {
    return parseInt(
      v.length === 4 ? v[1]! + v[1]! + v[2]! + v[2]! + v[3]! + v[3]! : v.slice(1),
      16,
    );
  }
  return 0xf5f1e8;
}

// Apply theme before Three.js scene boots so background colour is correct on first paint.
applyTheme(resolveInitialTheme());
mountThemeToggle();

// ---------- State (initial key — must precede Chrome so the selector can read it) ----------

function isFigKey(raw: string | null): raw is FigKey {
  return raw !== null && Object.hasOwn(FIGURES, raw);
}

// `/3d-geometry/<figure>` (or `?exp=<figure>`, which the platform's iframe
// uses) deep-links a figure; unknown keys fall back to the default.
const DEMO_SLUG = '3d-geometry';
const requestedFig = readRequestedExperiment(DEMO_SLUG);
let currentFigKey: FigKey = isFigKey(requestedFig) ? requestedFig : 'cuboid';
onExperimentRequest(() => notifyHostExperiment(currentFigKey));

// ---------- Chrome ----------

const topbarHandle: TopbarHandle<FigKey> = mountTopbar(
  document.getElementById('topbar') as HTMLElement,
  {
    eyebrowKey: 'app.eyebrow',
    i18n,
    selector: {
      options: Object.keys(FIGURES).map((key) => ({
        value: key,
        labelKey: FIGURES[key]!.labelKey,
      })),
      value: currentFigKey,
      ariaLabelKey: 'panel.figure',
      onChange: (key) => {
        currentFigKey = key;
        loadFigure();
      },
    },
  },
);

mountLangPicker(document.getElementById('langPicker') as HTMLElement, i18n);

mountHelp({ lineKeys: ['hint.line1', 'hint.line2'], i18n });

mountRotateNudge();

const infoBadge = new InfoBadge(document.getElementById('infoBadge') as HTMLElement);

// ---------- Three.js scene setup ----------

const stage = document.getElementById('stage') as HTMLElement;
const scene = new THREE.Scene();
scene.background = new THREE.Color(readThemeBgHex());

const camera = new THREE.PerspectiveCamera(40, window.innerWidth / window.innerHeight, 0.1, 100);
camera.position.set(6, 4.5, 7);

const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.localClippingEnabled = true;
stage.appendChild(renderer.domElement);

const labelRenderer = new CSS2DRenderer();
labelRenderer.setSize(window.innerWidth, window.innerHeight);
labelRenderer.domElement.style.position = 'fixed';
labelRenderer.domElement.style.inset = '0';
labelRenderer.domElement.style.pointerEvents = 'none';
labelRenderer.domElement.style.zIndex = '5';
document.body.appendChild(labelRenderer.domElement);

const controls = new OrbitControls(camera, renderer.domElement);
controls.enableDamping = true;
controls.dampingFactor = 0.08;
controls.minDistance = 3;
controls.maxDistance = 20;

scene.add(new THREE.AmbientLight(0xffffff, 0.55));
const dir = new THREE.DirectionalLight(0xffffff, 0.9);
dir.position.set(6, 10, 7);
scene.add(dir);
const fill = new THREE.DirectionalLight(0xc9d4e8, 0.25);
fill.position.set(-5, 3, -4);
scene.add(fill);

// ---------- State ----------

let dims: Record<string, number> = {};
let selectedLine: { kind: 'face' | 'space'; idx: number } | null = null;
let splitAmount = 0;
let animating = false;
let figData: FigureData;
let currentCutPlane: CutPlane | null = null;

// Style state
let bodyColor = 0xc9d4e8;
let bodyOpacity = 0.82;
let lineColor = 0xd93a3a;
let lineOpacity = 1.0;
let cutColor = 0xf2a9a9;
let cutOpacity = 1.0;
let showLabels = true;

// ---------- Groups ----------

const boxGroup = new THREE.Group();
scene.add(boxGroup);
const halfAGroup = new THREE.Group();
const halfBGroup = new THREE.Group();
boxGroup.add(halfAGroup, halfBGroup);
const linesOverlay = new THREE.Group();
boxGroup.add(linesOverlay);

// ---------- Clip planes ----------

const clipPlaneA = new THREE.Plane(new THREE.Vector3(1, 0, 0), 0);
const clipPlaneB = new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0);

// ---------- Shared materials ----------

const bodyMatA = new THREE.MeshStandardMaterial({
  color: 0xc9d4e8,
  roughness: 0.55,
  metalness: 0.02,
  transparent: true,
  opacity: 0.82,
  side: THREE.DoubleSide,
  clippingPlanes: [clipPlaneA],
});
const bodyMatB = new THREE.MeshStandardMaterial({
  color: 0xc9d4e8,
  roughness: 0.55,
  metalness: 0.02,
  transparent: true,
  opacity: 0.82,
  side: THREE.DoubleSide,
  clippingPlanes: [clipPlaneB],
});
const cutMat = new THREE.MeshStandardMaterial({
  color: 0xf2a9a9,
  roughness: 0.7,
  metalness: 0.0,
  side: THREE.DoubleSide,
});
const edgeMat = new THREE.LineBasicMaterial({ color: 0x1a1a1a });
const edgeMatA = new THREE.LineBasicMaterial({
  color: 0x1a1a1a,
  clippingPlanes: [clipPlaneA],
});
const edgeMatB = new THREE.LineBasicMaterial({
  color: 0x1a1a1a,
  clippingPlanes: [clipPlaneB],
});

// ---------- Dim controls ----------

const dimsContainer = document.getElementById('dimsContainer') as HTMLElement;
const dimRowHandles: Map<string, SliderRowHandle> = new Map();

function buildDimControls(preserve: boolean): void {
  dimsContainer.innerHTML = '';
  dimRowHandles.clear();
  const figDef = FIGURES[currentFigKey]!;
  const prev = preserve ? dims : null;
  dims = {};
  for (const p of figDef.params) {
    dims[p.id] = prev && prev[p.id] != null ? prev[p.id]! : p.value;
    const row = createSliderRow({
      id: p.id,
      labelKey: p.labelKey,
      i18n,
      min: p.min,
      max: p.max,
      step: p.step,
      value: dims[p.id]!,
      onChange: (v) => {
        dims[p.id] = v;
        rebuild();
      },
    });
    dimsContainer.appendChild(row.element);
    dimRowHandles.set(p.id, row);
  }
}

// ---------- Style controls ----------

const styleContainer = document.getElementById('styleContainer') as HTMLElement;
const hexToInt = (h: string): number => parseInt(h.replace('#', ''), 16);
const intToHex = (n: number): string => '#' + n.toString(16).padStart(6, '0');

styleContainer.appendChild(
  createStyleRow({
    labelKey: 'style.body',
    i18n,
    color: intToHex(bodyColor),
    opacity: bodyOpacity,
    onColorChange: (h) => {
      bodyColor = hexToInt(h);
      applyStyles();
    },
    onOpacityChange: (v) => {
      bodyOpacity = v;
      applyStyles();
    },
  }).element,
);

styleContainer.appendChild(
  createStyleRow({
    labelKey: 'style.line',
    i18n,
    color: intToHex(lineColor),
    opacity: lineOpacity,
    onColorChange: (h) => {
      lineColor = hexToInt(h);
      applyStyles();
    },
    onOpacityChange: (v) => {
      lineOpacity = v;
      applyStyles();
    },
  }).element,
);

styleContainer.appendChild(
  createStyleRow({
    labelKey: 'style.cut',
    i18n,
    color: intToHex(cutColor),
    opacity: cutOpacity,
    onColorChange: (h) => {
      cutColor = hexToInt(h);
      applyStyles();
    },
    onOpacityChange: (v) => {
      cutOpacity = v;
      applyStyles();
    },
  }).element,
);

styleContainer.appendChild(
  createToggleRow({
    labelKey: 'style.labels',
    i18n,
    checked: showLabels,
    onChange: (v) => {
      showLabels = v;
      rebuildGeometry();
      applySplit();
    },
  }).element,
);

// ---------- Line list ----------

const faceLinesEl = document.getElementById('faceLines') as HTMLElement;
const spaceLinesEl = document.getElementById('spaceLines') as HTMLElement;
const faceCount = document.getElementById('faceCount') as HTMLElement;
const spaceCount = document.getElementById('spaceCount') as HTMLElement;

function lineEndpoints(l: SpecialLine): [THREE.Vector3, THREE.Vector3] {
  if ('v1' in l && typeof l.v1 === 'number' && typeof l.v2 === 'number') {
    return [figData.vertices[l.v1] as THREE.Vector3, figData.vertices[l.v2] as THREE.Vector3];
  }
  if ('a' in l) {
    return [l.a, l.b];
  }
  // fallback — should not occur given well-formed SpecialLine
  return [new THREE.Vector3(), new THREE.Vector3()];
}

function lineLabel(l: SpecialLine): string {
  if (l.name) return l.name;
  if ('v1' in l && l.v1 != null && l.v2 != null) {
    const a = figData.vertices[l.v1] as Vertex;
    const b = figData.vertices[l.v2] as Vertex;
    return `${a.name ?? ''}${b.name ?? ''}`;
  }
  return '—';
}

function lineDetail(l: SpecialLine, kind: 'face' | 'space'): string {
  if (l.info) {
    if (l.info.startsWith('info.')) return i18n.t(l.info as Parameters<typeof i18n.t>[0]);
    return l.info;
  }
  return kind === 'face'
    ? i18n.t('info.face' as Parameters<typeof i18n.t>[0])
    : i18n.t('info.center' as Parameters<typeof i18n.t>[0]);
}

function makeLineButton(type: 'face' | 'space', line: SpecialLine, i: number): HTMLButtonElement {
  const btn = document.createElement('button');
  btn.className = 'line-btn';
  btn.dataset['type'] = type;
  btn.dataset['idx'] = String(i);
  btn.innerHTML = `
    <span class="swatch"></span>
    <span class="name">${lineLabel(line)}</span>
    <span class="detail">${lineDetail(line, type)}</span>
  `;
  btn.addEventListener('click', () => selectLine(type, i));
  return btn;
}

function buildLineList(): void {
  faceLinesEl.innerHTML = '';
  spaceLinesEl.innerHTML = '';
  const fd = figData.specialLines.face;
  const sd = figData.specialLines.space;
  faceCount.textContent = String(fd.length);
  spaceCount.textContent = String(sd.length);
  fd.forEach((l, i) => faceLinesEl.appendChild(makeLineButton('face', l, i)));
  sd.forEach((l, i) => spaceLinesEl.appendChild(makeLineButton('space', l, i)));
}

function refreshButtonStates(): void {
  document.querySelectorAll('.line-btn').forEach((b) => {
    const btn = b as HTMLElement;
    const active =
      selectedLine !== null &&
      btn.dataset['type'] === selectedLine.kind &&
      parseInt(btn.dataset['idx'] ?? '-1') === selectedLine.idx;
    btn.classList.toggle('active', active);
  });
}

// ---------- Disposal ----------

function disposeGroup(g: THREE.Group): void {
  const toRemove: THREE.Object3D[] = [];
  g.traverse((obj) => {
    if (obj !== g) toRemove.push(obj);
  });
  toRemove.forEach((obj) => {
    const css2d = obj as CSS2DObject;
    if (css2d.element && css2d.element.parentNode) {
      css2d.element.parentNode.removeChild(css2d.element);
    }
    if (obj.parent) obj.parent.remove(obj);
    const mesh = obj as THREE.Mesh;
    if (mesh.geometry) mesh.geometry.dispose();
    if (
      mesh.material &&
      (mesh.material as THREE.Material).dispose &&
      mesh.material !== bodyMatA &&
      mesh.material !== bodyMatB &&
      mesh.material !== cutMat &&
      mesh.material !== edgeMat &&
      mesh.material !== edgeMatA &&
      mesh.material !== edgeMatB
    ) {
      (mesh.material as THREE.Material).dispose();
    }
  });
}

// ---------- Cut plane math ----------

function polyArea(poly: THREE.Vector3[]): number {
  if (!poly || poly.length < 3) return 0;
  // Newell's shoelace in 3D
  const n = new THREE.Vector3();
  for (let k = 0; k < poly.length; k++) {
    const a = poly[k]!;
    const b = poly[(k + 1) % poly.length]!;
    n.x += (a.y - b.y) * (a.z + b.z);
    n.y += (a.z - b.z) * (a.x + b.x);
    n.z += (a.x - b.x) * (a.y + b.y);
  }
  return n.length() * 0.5;
}

// polyArea is available for external consumers; suppress unused-variable lint if needed.
void polyArea;

function computeCutPlane(): CutPlane {
  const list = figData.specialLines[selectedLine!.kind];
  const line = list[selectedLine!.idx]!;
  const [p1, p2] = lineEndpoints(line);
  const mid = p1.clone().add(p2).multiplyScalar(0.5);
  const lineDir = p2.clone().sub(p1).normalize();

  // Reference centroid (all figures are built centered at origin).
  const centroid = new THREE.Vector3(0, 0, 0);

  // Plane is determined by p1, p2, centroid — normal = (p2−p1) × (centroid−p1).
  const v1 = p2.clone().sub(p1);
  const v2 = centroid.clone().sub(p1);
  let normal = new THREE.Vector3().crossVectors(v1, v2);

  if (normal.lengthSq() < 1e-8) {
    // Degenerate: the line passes through the centroid (e.g. cuboid space diagonal,
    // cylinder axis). Any plane through the line is a valid bisector — pick the one
    // whose normal is most axis-aligned, for visually clean rectangular sections.
    const u = new THREE.Vector3(1, 0, 0);
    if (Math.abs(u.dot(lineDir)) > 0.95) u.set(0, 1, 0);
    u.crossVectors(lineDir, u).normalize();
    const w = new THREE.Vector3().crossVectors(lineDir, u).normalize();

    const axes = [
      new THREE.Vector3(1, 0, 0),
      new THREE.Vector3(0, 1, 0),
      new THREE.Vector3(0, 0, 1),
    ];
    let bestAlign = -1;
    const N = 90;
    for (let k = 0; k < N; k++) {
      const a = (k / N) * Math.PI;
      const cand = u
        .clone()
        .multiplyScalar(Math.cos(a))
        .addScaledVector(w, Math.sin(a))
        .normalize();
      const align = Math.max(...axes.map((ax) => Math.abs(cand.dot(ax))));
      if (align > bestAlign) {
        bestAlign = align;
        normal = cand;
      }
    }
  } else {
    normal.normalize();
  }

  return { normal, mid, p1, p2, lineDir };
}

// ---------- Rebuild ----------

function loadFigure(): void {
  topbarHandle.setSelectorValue(currentFigKey);
  buildDimControls(false);
  selectedLine = null;
  splitAmount = 0;
  splitSlider.value = '0';
  splitSlider.disabled = true;
  animateBtn.disabled = true;
  rebuild();
  writeExperimentUrl(DEMO_SLUG, currentFigKey);
  notifyHostExperiment(currentFigKey);
}

function rebuild(): void {
  const figDef = FIGURES[currentFigKey]!;
  figData = figDef.build(dims);
  buildLineList();
  rebuildGeometry();
  refreshButtonStates();
  applySplit();
}

function rebuildGeometry(): void {
  disposeGroup(halfAGroup);
  disposeGroup(halfBGroup);
  disposeGroup(linesOverlay);

  if (!selectedLine) {
    bodyMatA.clippingPlanes = null;
    edgeMatA.clippingPlanes = null;
    const mesh = new THREE.Mesh(figData.bodyGeometry, bodyMatA);
    halfAGroup.add(mesh);
    // edges (for polyhedra)
    if (figData.edges && figData.edges.length) {
      const pos: number[] = [];
      for (const [i, j] of figData.edges) {
        const a = figData.vertices[i]!;
        const b = figData.vertices[j]!;
        pos.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
      const g = new THREE.BufferGeometry();
      g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      halfAGroup.add(new THREE.LineSegments(g, edgeMat));
    } else {
      // use EdgesGeometry for curved bodies (shows silhouette boundaries)
      const eg = new THREE.EdgesGeometry(figData.bodyGeometry, 30);
      halfAGroup.add(new THREE.LineSegments(eg, edgeMat));
    }
    currentCutPlane = null;
  } else {
    bodyMatA.clippingPlanes = [clipPlaneA];
    edgeMatA.clippingPlanes = [clipPlaneA];
    const plane = computeCutPlane();
    currentCutPlane = plane;
    const nMid = plane.normal.dot(plane.mid);
    clipPlaneA.normal.copy(plane.normal);
    clipPlaneA.constant = -nMid;
    clipPlaneB.normal.copy(plane.normal).negate();
    clipPlaneB.constant = nMid;

    const geoA = figData.bodyGeometry;
    const geoB = geoA.clone();
    halfAGroup.add(new THREE.Mesh(geoA, bodyMatA));
    halfBGroup.add(new THREE.Mesh(geoB, bodyMatB));

    // edges
    if (figData.edges && figData.edges.length) {
      const pos: number[] = [];
      for (const [i, j] of figData.edges) {
        const a = figData.vertices[i]!;
        const b = figData.vertices[j]!;
        pos.push(a.x, a.y, a.z, b.x, b.y, b.z);
      }
      const g1 = new THREE.BufferGeometry();
      g1.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
      const g2 = g1.clone();
      halfAGroup.add(new THREE.LineSegments(g1, edgeMatA));
      halfBGroup.add(new THREE.LineSegments(g2, edgeMatB));
    } else {
      const eg = new THREE.EdgesGeometry(geoA, 30);
      halfAGroup.add(new THREE.LineSegments(eg, edgeMatA));
      halfBGroup.add(new THREE.LineSegments(eg.clone(), edgeMatB));
    }

    // cut polygon
    const poly = figData.cutPoly(plane);
    if (poly && poly.length >= 3) {
      const positions: number[] = [];
      const normals: number[] = [];
      for (let k = 1; k < poly.length - 1; k++) {
        const p0 = poly[0]!;
        const pk = poly[k]!;
        const pk1 = poly[k + 1]!;
        positions.push(p0.x, p0.y, p0.z, pk.x, pk.y, pk.z, pk1.x, pk1.y, pk1.z);
        const n = plane.normal;
        normals.push(n.x, n.y, n.z, n.x, n.y, n.z, n.x, n.y, n.z);
      }
      const cg = new THREE.BufferGeometry();
      cg.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
      cg.setAttribute('normal', new THREE.Float32BufferAttribute(normals, 3));
      halfAGroup.add(new THREE.Mesh(cg, cutMat));
      halfBGroup.add(new THREE.Mesh(cg.clone(), cutMat));

      const outlinePts: number[] = [];
      for (let k = 0; k < poly.length; k++) {
        const p = poly[k]!;
        const q = poly[(k + 1) % poly.length]!;
        outlinePts.push(p.x, p.y, p.z, q.x, q.y, q.z);
      }
      const og = new THREE.BufferGeometry();
      og.setAttribute('position', new THREE.Float32BufferAttribute(outlinePts, 3));
      halfAGroup.add(new THREE.LineSegments(og, edgeMat));
      halfBGroup.add(new THREE.LineSegments(og.clone(), edgeMat));
    }
  }

  // Red selected line — added to BOTH halves so it stays attached after separation
  if (selectedLine) {
    const list = figData.specialLines[selectedLine.kind];
    const line = list[selectedLine.idx]!;
    const [p1, p2] = lineEndpoints(line);
    const len = p1.distanceTo(p2);
    const up = new THREE.Vector3(0, 1, 0);
    const dir2 = p2.clone().sub(p1).normalize();
    const mid = p1.clone().add(p2).multiplyScalar(0.5);
    const quat = new THREE.Quaternion().setFromUnitVectors(up, dir2);
    const parents = currentCutPlane ? [halfAGroup, halfBGroup] : [linesOverlay];
    for (const parent of parents) {
      const tube = new THREE.Mesh(
        new THREE.CylinderGeometry(0.035, 0.035, len, 20),
        new THREE.MeshStandardMaterial({
          color: lineColor,
          roughness: 0.4,
          transparent: true,
          opacity: lineOpacity,
        }),
      );
      tube.userData['isSelectedLine'] = true;
      tube.position.copy(mid);
      tube.quaternion.copy(quat);
      parent.add(tube);
      for (const p of [p1, p2]) {
        const s = new THREE.Mesh(
          new THREE.SphereGeometry(0.07, 16, 16),
          new THREE.MeshStandardMaterial({
            color: lineColor,
            roughness: 0.35,
            transparent: true,
            opacity: lineOpacity,
          }),
        );
        s.userData['isSelectedLine'] = true;
        s.position.copy(p);
        parent.add(s);
      }
    }
  }

  // Vertex labels & dots
  if (figData.vertices && figData.vertices.length) {
    const endpointSet = new Set<THREE.Vector3>();
    if (selectedLine) {
      const line = figData.specialLines[selectedLine.kind][selectedLine.idx]!;
      const [ep1, ep2] = lineEndpoints(line);
      endpointSet.add(ep1);
      endpointSet.add(ep2);
    }
    for (const v of figData.vertices) {
      if (!v.name) continue;
      if (!selectedLine || !currentCutPlane) {
        addVertexDot(linesOverlay, v);
        continue;
      }
      const s = v.clone().sub(currentCutPlane.mid).dot(currentCutPlane.normal);
      const isEndpoint = endpointSet.has(v);
      const onPlane = Math.abs(s) < 1e-4;
      if (isEndpoint || onPlane) {
        addVertexDot(halfAGroup, v);
        addVertexDot(halfBGroup, v);
      } else if (s > 0) addVertexDot(halfAGroup, v);
      else addVertexDot(halfBGroup, v);
    }
  }

  updateInfoBadge();
  applyStyles();
  // Force an immediate render so CSS2D labels appear without waiting for the
  // next animation frame (which can be throttled when the tab is hidden).
  renderer.render(scene, camera);
  labelRenderer.render(scene, camera);
}

function addVertexDot(parent: THREE.Group, v: Vertex): void {
  const s = new THREE.Mesh(
    new THREE.SphereGeometry(0.04, 12, 12),
    new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.5 }),
  );
  s.position.copy(v);
  parent.add(s);
  if (!showLabels) return;
  const el = document.createElement('div');
  el.className = 'vertex-label';
  el.textContent = v.name ?? '';
  const lbl = new CSS2DObject(el);
  lbl.position.copy(v);
  parent.add(lbl);
}

// ---------- Split ----------

function applySplit(): void {
  if (!currentCutPlane) {
    halfAGroup.position.set(0, 0, 0);
    halfBGroup.position.set(0, 0, 0);
    linesOverlay.position.set(0, 0, 0);
    applyStyles();
    updateInfoBadge();
    return;
  }
  const dist = splitAmount * 1.4;
  const offset = currentCutPlane.normal.clone().multiplyScalar(dist);
  halfAGroup.position.copy(offset);
  halfBGroup.position.copy(offset.clone().negate());
  const nMid = currentCutPlane.normal.dot(currentCutPlane.mid);
  clipPlaneA.constant = -(nMid + dist);
  clipPlaneB.constant = nMid - dist;
  applyStyles();
  updateInfoBadge();
}

function applyStyles(): void {
  bodyMatA.color.setHex(bodyColor);
  bodyMatB.color.setHex(bodyColor);
  bodyMatA.opacity = bodyOpacity;
  bodyMatB.opacity = bodyOpacity;
  bodyMatA.transparent = true;
  bodyMatB.transparent = true;
  cutMat.color.setHex(cutColor);
  cutMat.opacity = cutOpacity;
  cutMat.transparent = cutOpacity < 1;
  const updateLine = (o: THREE.Object3D) => {
    const mesh = o as THREE.Mesh;
    if (mesh.userData['isSelectedLine'] && mesh.material) {
      const mat = mesh.material as THREE.MeshStandardMaterial;
      mat.color.setHex(lineColor);
      mat.transparent = true;
      mat.opacity = lineOpacity;
    }
  };
  halfAGroup.traverse(updateLine);
  halfBGroup.traverse(updateLine);
  linesOverlay.traverse(updateLine);
}

function updateInfoBadge(): void {
  if (!selectedLine) {
    infoBadge.hide();
    return;
  }
  const line = figData.specialLines[selectedLine.kind][selectedLine.idx]!;
  const [p1, p2] = lineEndpoints(line);
  infoBadge.show({
    name: `${lineLabel(line)} · ${lineDetail(line, selectedLine.kind)}`,
    value: `ℓ = ${p1.distanceTo(p2).toFixed(2)}`,
  });
}

// ---------- Select line ----------

function selectLine(type: 'face' | 'space', idx: number): void {
  if (selectedLine && selectedLine.kind === type && selectedLine.idx === idx) {
    selectedLine = null;
    splitAmount = 0;
    splitSlider.value = '0';
    splitSlider.disabled = true;
    animateBtn.disabled = true;
  } else {
    selectedLine = { kind: type, idx };
    splitSlider.disabled = false;
    animateBtn.disabled = false;
  }
  rebuildGeometry();
  applySplit();
  refreshButtonStates();
}

// ---------- Wiring ----------

const splitSlider = document.getElementById('splitSlider') as HTMLInputElement;
const animateBtn = document.getElementById('animateBtn') as HTMLButtonElement;
const resetBtn = document.getElementById('resetBtn') as HTMLButtonElement;

splitSlider.addEventListener('input', (e) => {
  splitAmount = parseFloat((e.target as HTMLInputElement).value);
  applySplit();
});

animateBtn.addEventListener('click', () => {
  if (animating) return;
  animating = true;
  const start = performance.now();
  const from = splitAmount;
  const to = splitAmount < 0.5 ? 1 : 0;
  const dur = 900;
  const tick = () => {
    const t = Math.min(1, (performance.now() - start) / dur);
    const eased = t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
    splitAmount = from + (to - from) * eased;
    splitSlider.value = String(splitAmount);
    applySplit();
    if (t < 1) requestAnimationFrame(tick);
    else animating = false;
  };
  requestAnimationFrame(tick);
});

resetBtn.addEventListener('click', () => {
  selectedLine = null;
  splitAmount = 0;
  splitSlider.value = '0';
  splitSlider.disabled = true;
  animateBtn.disabled = true;
  rebuildGeometry();
  applySplit();
  refreshButtonStates();
  camera.position.set(6, 4.5, 7);
  controls.target.set(0, 0, 0);
  controls.update();
});

window.addEventListener('resize', () => {
  camera.aspect = window.innerWidth / window.innerHeight;
  camera.updateProjectionMatrix();
  renderer.setSize(window.innerWidth, window.innerHeight);
  labelRenderer.setSize(window.innerWidth, window.innerHeight);
});

// Subscribe to language changes — rebuild line buttons (they contain translated text).
i18n.subscribe(() => buildLineList());

// React to theme messages from the platform iframe host.
onThemeMessage((next) => {
  applyTheme(next);
});

// Three.js doesn't observe CSS variables, so the WebGL scene background must
// be re-read whenever data-theme flips — whether the change came from the
// platform postMessage or from the standalone theme-toggle button.
const themeObserver = new MutationObserver(() => {
  scene.background = new THREE.Color(readThemeBgHex());
});
themeObserver.observe(document.documentElement, {
  attributes: true,
  attributeFilter: ['data-theme'],
});

// ---------- Init ----------

loadFigure();

// ---------- Screenshot preset (capture pipeline) ----------
function applyShot(id: string): void {
  switch (id) {
    case 'cube-diagonal': {
      currentFigKey = 'cube';
      loadFigure();
      // Pick the first space (body) diagonal so the cut polygon is visible.
      if (figData.specialLines.space.length > 0) selectLine('space', 0);
      splitAmount = 0.5;
      splitSlider.value = '0.5';
      applySplit();
      break;
    }
    case 'cuboid-altitude': {
      currentFigKey = 'cuboid';
      loadFigure();
      if (figData.specialLines.space.length > 0) selectLine('space', 0);
      splitAmount = 0.5;
      splitSlider.value = '0.5';
      applySplit();
      break;
    }
    case 'tri-prism-edge': {
      currentFigKey = 'triPrism';
      loadFigure();
      if (figData.specialLines.face.length > 0) selectLine('face', 0);
      splitAmount = 0.4;
      splitSlider.value = '0.4';
      applySplit();
      break;
    }
    case 'pyramid-slant': {
      currentFigKey = 'pyramid4';
      loadFigure();
      if (figData.specialLines.face.length > 0) selectLine('face', 0);
      splitAmount = 0.4;
      splitSlider.value = '0.4';
      applySplit();
      break;
    }
    case 'cylinder-axis': {
      currentFigKey = 'cylinder';
      loadFigure();
      if (figData.specialLines.space.length > 0) selectLine('space', 0);
      splitAmount = 0.5;
      splitSlider.value = '0.5';
      applySplit();
      break;
    }
    case 'cylinder-tilt': {
      currentFigKey = 'cylinder';
      loadFigure();
      // Pick a non-axis line if one exists; fall back to space[0].
      const list = figData.specialLines.space;
      const lineIdx = list.length > 1 ? 1 : 0;
      if (list.length > 0) selectLine('space', lineIdx);
      splitAmount = 0.5;
      splitSlider.value = '0.5';
      applySplit();
      break;
    }
    default: {
      console.warn(`[shot] unknown preset id: ${id}`);
    }
  }
}

const shotId = getShotId();
if (shotId !== null) {
  markShotMode();
  applyShot(shotId);
}

// ---------- Demo readiness (for visual-regression tests) ----------

declare global {
  interface Window {
    __demoReady?: Promise<void>;
  }
}

let resolveReady!: () => void;
window.__demoReady = new Promise<void>((res) => {
  resolveReady = res;
});

// ---------- Render loop ----------

function renderLoop(): void {
  controls.update();
  renderer.render(scene, camera);
  labelRenderer.render(scene, camera);
  requestAnimationFrame(renderLoop);
}
renderLoop();

// Resolve __demoReady after two frames (first builds, second renders into the canvas).
requestAnimationFrame(() => requestAnimationFrame(() => resolveReady()));

const dimsPanel = document.querySelector('.dims-panel') as HTMLElement;
const linesPanel = document.querySelector('.lines-panel') as HTMLElement;

mountMobileDrawer(dimsPanel, { side: 'left', icon: 'controls' });
mountMobileDrawer(linesPanel, { side: 'right', icon: 'info' });

makeCollapsible(dimsPanel, {
  initiallyOpen: true,
  storageKey: 'learnforge:collapsible:3d-geometry:dims',
});
makeCollapsible(linesPanel, {
  initiallyOpen: true,
  storageKey: 'learnforge:collapsible:3d-geometry:lines',
});
